import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface GlobalMetrics {
  totalTeams: number;
  totalSessions: number;
  totalPracticeHours: number;
  equivalentTrainingHours: number;
  fullPracticesGained: number;
  totalTouches: number;
  soccerIQScenarios: number;
  totalSoccerIQXP: number;
}

export function useGlobalMetrics() {
  return useQuery({
    queryKey: ['global-metrics'],
    queryFn: async (): Promise<GlobalMetrics> => {
      // Call the security definer function to get global metrics (bypasses RLS)
      const { data, error } = await supabase.rpc('get_global_metrics');

      if (error) throw error;

      const metrics = data as { total_teams: number; total_sessions: number; total_practice_seconds: number; total_touches: number; soccer_iq_scenarios: number; total_soccer_iq_xp: number } | null;
      const totalTeams = metrics?.total_teams || 0;
      const totalSessions = metrics?.total_sessions || 0;
      const totalPracticeSeconds = metrics?.total_practice_seconds || 0;
      const totalTouches = metrics?.total_touches || 0;
      const soccerIQScenarios = metrics?.soccer_iq_scenarios || 0;
      const totalSoccerIQXP = metrics?.total_soccer_iq_xp || 0;
      const totalPracticeHours = totalPracticeSeconds / 3600;

      console.debug('🌍 Global metrics from database:', {
        totalTeams,
        totalSessions,
        totalPracticeSeconds,
        totalPracticeHours: Math.round(totalPracticeHours * 10) / 10,
        totalTouches
      });

      // Calculate equivalent training hours (focused individual training is ~1.5x more effective)
      const equivalentTrainingHours = totalPracticeHours * 1.5;

      // Calculate full practices gained (assume 1 practice = 90 minutes = 1.5 hours)
      const fullPracticesGained = Math.floor(totalPracticeHours / 1.5);

      return {
        totalTeams,
        totalSessions,
        totalPracticeHours,
        equivalentTrainingHours,
        fullPracticesGained,
        totalTouches,
        soccerIQScenarios,
        totalSoccerIQXP
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}
